import {
  BaseModel,
  ReadonlyWatchable,
  TargetableModel,
  useInScopeModel,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import {
  MixedSourceWatchable,
  type ReadonlyEventWatchable,
} from '@cotera/client/v0/actions/framework/watchable';
import {
  ChatAction,
  MessageClass,
  ToolPart,
  type ChatActionData,
} from '@cotera/sdk/core';
import type { ChatStatusFrameOutput } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import type { Conversation } from '@cotera/sdk/core';
import type { UIMessage } from 'ai';
import {
  getStackFrameStatusInfo,
  getLastErrorFromStackFrames,
  sumCreditsFromFrameTree,
} from '../chat/utils/coco-stack-frame-status';
import { type ChatStatus } from '../chat/models/coco-chat.model';
import { WatchablesFactory } from '../watchables/watchables-factory';

type SubAgentSnapshotAction = {
  id: string;
  chatId: string;
  type: string;
  status: string;
  data: Record<string, unknown> | null;
  completedAt: Date | string | null;
  approvedAt: Date | string | null;
  approvedBy: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
};

type SubAgentSnapshot = {
  stackFrames: ChatStatusFrameOutput[];
  messages: Array<{ id: string; message?: unknown; createdAt?: Date | string }>;
  actions: SubAgentSnapshotAction[];
};

/**
 * this is needed because stack frames get garabage collected more often that conversations do
 * @returns
 */
export function shouldTreatNoStackFramesAsCompleted(
  snapshot: SubAgentSnapshot
): boolean {
  const lastMessage = snapshot.messages.at(-1)?.message;
  if (lastMessage === undefined || lastMessage === null) {
    return false;
  }
  if (typeof lastMessage !== 'object') {
    return false;
  }
  const role = (lastMessage as { role?: unknown }).role;
  return role === 'assistant';
}

function toDate(v: Date | string | null): Date | null {
  if (v === null) {
    return null;
  }
  return v instanceof Date ? v : new Date(v);
}

function convertActionsToChatActions(
  actions: SubAgentSnapshotAction[]
): ChatAction[] {
  return actions.map((a) =>
    ChatAction.fromData({
      id: a.id,
      actorId: null,
      chatId: a.chatId,
      type: a.type as ChatActionData['type'],
      status: a.status as ChatActionData['status'],
      data: a.data,
      completedAt: toDate(a.completedAt),
      approvedAt: toDate(a.approvedAt),
      approvedBy: a.approvedBy,
      createdAt: toDate(a.createdAt)!,
      updatedAt: toDate(a.updatedAt)!,
    })
  );
}

function convertListMessagesToMessageClass(
  chatId: string,
  messages: Array<{ id: string; message?: unknown; createdAt?: Date | string }>,
  actions: ChatAction[]
): MessageClass[] {
  return messages
    .filter(
      (
        msg
      ): msg is { id: string; message: unknown; createdAt?: Date | string } =>
        msg.message !== undefined
    )
    .map((msg) => {
      const uiMessage = msg.message as UIMessage;
      const type =
        uiMessage.id?.startsWith('compaction') === true ? 'compaction' : 'chat';
      // Without this meta the Message constructor falls back to `new Date()`,
      // so every snapshot recompute re-stamps timestamps to the current time.
      const createdAt = toDate(msg.createdAt ?? null);
      return MessageClass.fromUIMessage(
        chatId,
        uiMessage,
        type,
        actions,
        createdAt !== null ? { createdAt } : undefined
      );
    });
}

export type SubAgentToolModelProps = {
  part: ToolPart;
  /** When provided, enables event-driven sub-agent data via WebSocket. Only requires client. */
  eventConfig?: {
    client: TenantedClient['resource']['chat'];
  };
  /** Override conversation ID (for for-each sub-agents with known chat IDs) */
  conversationIdOverride?: string;
  /** Override model ID (default: part.toolCallId). Must be unique per model in scope. */
  modelId?: string;
  watchablesFactory: Pick<
    WatchablesFactory,
    'makeStackFrameWrittenWatchable' | 'makeSubAgentSnapshotWatchable'
  >;
};

const EMPTY_SNAPSHOT: SubAgentSnapshot = {
  stackFrames: [],
  messages: [],
  actions: [],
};

export class SubAgentToolModel extends BaseModel implements TargetableModel {
  private _isTargeted = true;

  readonly conversationId: string | null;
  readonly isIncomplete: boolean;

  readonly conversationWatchable: ReadonlyWatchable<Conversation | null>;
  readonly loadedToolsWatchable: ReadonlyWatchable<string[]>;
  readonly hasMessages: ReadonlyWatchable<boolean>;
  readonly messagesWatchable: ReadonlyWatchable<MessageClass[]>;
  readonly messagesStatusWatchable: ReadonlyWatchable<'loading' | 'ready'>;
  readonly statusWatchable: MixedSourceWatchable<ChatStatus>;
  /** Same stack-frame selection rules as the main Coco header (cancellation wins, etc.). */
  readonly stackFrameStatusInfoWatchable: ReadonlyWatchable<
    ReturnType<typeof getStackFrameStatusInfo>
  >;
  readonly errorWatchable: MixedSourceWatchable<Error | undefined>;
  readonly creditsWatchable: ReadonlyWatchable<number>;
  readonly snapshotWatchable: ReadonlyEventWatchable<SubAgentSnapshot>;

  constructor(props: SubAgentToolModelProps) {
    super(props.modelId ?? props.part.toolCallId);
    this.conversationId =
      props.conversationIdOverride ?? extractSubAgentChatId(props.part);
    this.isIncomplete = props.part.isIncomplete();

    const watchablesFactory = props.watchablesFactory;

    if (props.eventConfig !== undefined && this.conversationId !== null) {
      const { client } = props.eventConfig;
      const conversationId = this.conversationId;

      const snapshotEventWatchable =
        watchablesFactory.makeSubAgentSnapshotWatchable<
          SubAgentSnapshot,
          {
            chatId?: string;
            conversationId?: string;
            conversationAncestry?: string[];
          }
        >({
          runOnMount: true,
          initialValue: EMPTY_SNAPSHOT,
          filter: (payload) => {
            if (payload === undefined || payload === null) {
              return false;
            }
            const p = payload as Record<string, unknown>;
            if (p['chatId'] === conversationId) {
              return true;
            }
            if (p['conversationId'] === conversationId) {
              return true;
            }
            const ancestry = p['conversationAncestry'] as string[] | undefined;
            if (ancestry?.includes(conversationId) === true) {
              return true;
            }
            if (
              p['resourceType'] === 'chat' &&
              typeof p['resourceId'] === 'string' &&
              p['resourceId'] === conversationId &&
              (p['conversationAncestry'] !== undefined ||
                p['messagePosition'] !== undefined)
            ) {
              return true;
            }
            return false;
          },
          fn: async (_payload, _get) => {
            const res = await client.subAgentSnapshot({ conversationId });
            if (res.isOk()) {
              return {
                stackFrames: res.value.stackFrames,
                messages: res.value.messages,
                actions: (res.value.actions ?? []) as SubAgentSnapshotAction[],
              };
            }
            return EMPTY_SNAPSHOT;
          },
        });

      this.conversationWatchable = Watchable.fromValue(null);
      this.loadedToolsWatchable = Watchable.fromValue([]);
      this.hasMessages = Watchable.from((get) => {
        const snapshot = get(snapshotEventWatchable);
        return snapshot.messages.length > 0;
      });
      this.messagesWatchable = Watchable.from((get) => {
        const snapshot = get(snapshotEventWatchable);
        const actions = convertActionsToChatActions(snapshot.actions);
        return convertListMessagesToMessageClass(
          this.conversationId!,
          snapshot.messages,
          actions
        );
      });
      this.messagesStatusWatchable = Watchable.from((get) => {
        get(snapshotEventWatchable);
        return 'ready';
      });
      this.stackFrameStatusInfoWatchable = Watchable.from((get) => {
        const snapshot = get(snapshotEventWatchable);
        return getStackFrameStatusInfo(snapshot.stackFrames);
      });
      this.statusWatchable = MixedSourceWatchable.from((get) => {
        const snapshot = get(snapshotEventWatchable);
        if (snapshot.stackFrames.length === 0) {
          if (shouldTreatNoStackFramesAsCompleted(snapshot)) {
            return 'ready';
          }
          // Part already complete: don't flash a spinner while waiting for API response.
          return (this.isIncomplete ? 'submitted' : 'ready') as ChatStatus;
        }
        const frameInfo = getStackFrameStatusInfo(snapshot.stackFrames);
        if (
          frameInfo.statusKey === 'cancelled' ||
          (frameInfo.isComplete && frameInfo.statusKey !== 'failed')
        ) {
          return 'ready';
        }
        if (frameInfo.statusKey === 'failed') {
          return 'error';
        }
        return 'streaming';
      });
      this.errorWatchable = MixedSourceWatchable.from((get) => {
        const snapshot = get(snapshotEventWatchable);
        const lastError = getLastErrorFromStackFrames(snapshot.stackFrames);
        return lastError !== undefined ? new Error(lastError) : undefined;
      });
      this.creditsWatchable = Watchable.from((get) => {
        const snapshot = get(snapshotEventWatchable);
        return sumCreditsFromFrameTree(snapshot.stackFrames);
      });
      this.snapshotWatchable = snapshotEventWatchable;
    } else {
      this.conversationWatchable = Watchable.fromValue(null);
      this.loadedToolsWatchable = Watchable.fromValue([]);
      this.hasMessages = Watchable.fromValue(false);
      this.messagesWatchable = Watchable.fromValue([]);
      this.messagesStatusWatchable = Watchable.fromValue('loading');
      this.stackFrameStatusInfoWatchable = Watchable.fromValue(
        getStackFrameStatusInfo([])
      );
      this.statusWatchable = MixedSourceWatchable.from(
        () => 'submitted' as ChatStatus
      );
      this.errorWatchable = MixedSourceWatchable.from(() => undefined);
      this.creditsWatchable = Watchable.fromValue(0);
      this.snapshotWatchable =
        watchablesFactory.makeStackFrameWrittenWatchable<SubAgentSnapshot>({
          runOnMount: true,
          initialValue: EMPTY_SNAPSHOT,
          filter: () => false,
          fn: async (_payload, _get) => EMPTY_SNAPSHOT,
        });
    }
  }

  get isTargeted() {
    return this._isTargeted;
  }

  target(): void {
    this._isTargeted = true;
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }

  dispose(): void {
    this.snapshotWatchable.unsubscribe();
    this.statusWatchable.unsubscribe();
    this.errorWatchable.unsubscribe();
  }
}

type SubAgentOutput = {
  subAgentChatId?: string;
  '@@cotera-tool-incomplete'?: unknown;
};

export type SubAgentToolPartLike = {
  readonly input: Record<string, unknown> | undefined;
  getOutput: () => unknown;
  raw: () => unknown;
};

function parseSubAgentChatIdFromIncompleteMarker(raw: unknown): string | null {
  if (raw === undefined || raw === null) {
    return null;
  }
  let parsed: SubAgentOutput | undefined;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw) as SubAgentOutput;
    } catch {
      return null;
    }
  } else if (typeof raw === 'object') {
    parsed = raw as SubAgentOutput;
  }
  if (
    parsed !== undefined &&
    typeof parsed.subAgentChatId === 'string' &&
    parsed.subAgentChatId !== ''
  ) {
    return parsed.subAgentChatId;
  }
  return null;
}

function parseSubAgentChatIdFromRecord(record: unknown): string | null {
  if (record === null || record === undefined || typeof record !== 'object') {
    return null;
  }
  const obj = record as Record<string, unknown>;
  if (
    typeof obj['subAgentChatId'] === 'string' &&
    obj['subAgentChatId'] !== ''
  ) {
    return obj['subAgentChatId'];
  }
  return parseSubAgentChatIdFromIncompleteMarker(
    obj['@@cotera-tool-incomplete']
  );
}

export function extractSubAgentChatId(
  part: SubAgentToolPartLike
): string | null {
  const fromInput =
    part.input !== undefined
      ? parseSubAgentChatIdFromIncompleteMarker(
          part.input['@@cotera-tool-incomplete']
        )
      : null;
  if (fromInput !== null) {
    return fromInput;
  }

  const fromOutput = parseSubAgentChatIdFromRecord(part.getOutput());
  if (fromOutput !== null) {
    return fromOutput;
  }

  const raw = part.raw();
  if (raw === null || raw === undefined || typeof raw !== 'object') {
    return null;
  }
  const rawRecord = raw as Record<string, unknown>;

  const fromRawInput = parseSubAgentChatIdFromRecord(rawRecord['input']);
  if (fromRawInput !== null) {
    return fromRawInput;
  }

  return parseSubAgentChatIdFromRecord(rawRecord['output']);
}

export const useSubAgentToolModel = (toolCallId: string) =>
  useInScopeModel(SubAgentToolModel, toolCallId);
